import * as THREE from 'three';
import ParticleBackground from './scenes/ParticleBackground.js';
import Logo3D from './scenes/Logo3D.js';
import InteractiveScene from './scenes/InteractiveScene.js';
import MouseTracker from './utils/MouseTracker.js';
import PerformanceMonitor from './utils/PerformanceMonitor.js';

class WebGLManager {
  constructor(canvas) {
    this.canvas = canvas;
    this.scenes = [];
    this.rafId = null;

    this.init();
  }

  init() {
    // Setup renderer
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: window.devicePixelRatio === 1,
      alpha: true,
      powerPreference: 'high-performance'
    });

    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.autoClear = false;

    // Mouse tracker (shared across scenes)
    this.mouse = new MouseTracker();

    // Performance monitor
    this.perfMonitor = new PerformanceMonitor();

    // Initialize scenes
    this.initScenes();

    // Event listeners
    window.addEventListener('resize', this.onResize.bind(this));

    // Listen for low performance events
    window.addEventListener('low-performance', this.handleLowPerformance.bind(this));
  }

  initScenes() {
    const timestamp = new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 });
    console.log(`[${timestamp}] 🎨 WebGLManager: Initializing scenes...`);

    // Scene 1: Particle background (full screen, z-index 1) - DISABLED
    console.log(`[${timestamp}] 🎨 WebGLManager: ParticleBackground disabled to fix UI issues`);
    // DISABLED - causing UI issues
    // this.particleBackground = new ParticleBackground(this.renderer, this.mouse);
    // this.scenes.push(this.particleBackground);

    // Scene 2: 3D Logo in hero (positioned container) - TEMPORARILY DISABLED
    const logoContainer = document.getElementById('logo-3d-container');
    if (logoContainer) {
      console.log(`[${timestamp}] 🎨 WebGLManager: Logo3D container found, but skipping due to rendering issues`);
      // TEMPORARILY DISABLED - causing positioning issues on scroll
      // this.logo3D = new Logo3D(this.renderer, logoContainer);
      // this.scenes.push(this.logo3D);
    }

    // Scene 3: Interactive scene in portfolio - DISABLED
    const interactiveContainer = document.getElementById('interactive-scene-container');
    if (interactiveContainer) {
      console.log(`[${timestamp}] 🎨 WebGLManager: InteractiveScene disabled to fix UI issues`);
      // DISABLED - causing UI issues
      // this.interactiveScene = new InteractiveScene(this.renderer, interactiveContainer, this.mouse);
      // this.scenes.push(this.interactiveScene);
    }

    console.log(`[${timestamp}] 🎨 WebGLManager: Initialized ${this.scenes.length} scenes`);
  }

  start() {
    this.render();
  }

  render() {
    this.rafId = requestAnimationFrame(this.render.bind(this));

    // Update performance stats
    this.perfMonitor.begin();

    // Clear renderer
    this.renderer.clear();

    // Render each scene
    this.scenes.forEach(scene => {
      if (scene.isVisible && scene.isVisible()) {
        scene.update();
        scene.render(this.renderer);
      } else if (!scene.isVisible) {
        // Scene doesn't have visibility check, render anyway
        scene.update();
        scene.render(this.renderer);
      }
    });

    this.perfMonitor.end();
  }

  onResize() {
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.scenes.forEach(scene => {
      if (scene.onResize) {
        scene.onResize();
      }
    });
  }

  handleLowPerformance(event) {
    console.warn('Handling low performance, reducing quality...');
    // Optionally reduce particle count or disable some effects
    // This can be implemented based on specific needs
  }

  destroy() {
    cancelAnimationFrame(this.rafId);
    this.scenes.forEach(scene => {
      if (scene.destroy) {
        scene.destroy();
      }
    });
    this.renderer.dispose();
    window.removeEventListener('resize', this.onResize.bind(this));
  }
}

export default WebGLManager;
